import i18n from '@/utils/i18n';
import React, { useEffect, useMemo } from 'react';
import { createStyles, useTheme } from '@/contexts/theme-context';
import { formatCurrency, formatPercent } from '@/utils/money';
import { getVariantPackCostFromMap } from '@/utils/price-editing';
import {
  isAuthenticatedUserContext,
  useUserContext,
} from '@/contexts/user-context';
import { partnerSupplier } from '@/utils/environment';
import { Skeleton } from 'react-native-skeletons';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useGetOrderEstimatedCommission } from './hooks/use-get-order-estimated-commission';
import { useSupplierDynamicConfigFlag } from '@/hooks/use-dynamic-config-value';
import { useVariantPackInfoContext } from '@/contexts/variant-pack-info-context';
import { ValidateOrderFieldsFragment } from '@/types';
import { View } from 'react-native';

interface Props {
  isUpdating: boolean;
  order: ValidateOrderFieldsFragment;
}

export const SalesRepOrderSummary: React.FC<Props> = ({
  isUpdating,
  order,
}) => {
  const { currencyCode, getVariantPackInfo } = useVariantPackInfoContext();
  const showGrossProfit = !useSupplierDynamicConfigFlag(
    'sales_rep_config',
    'disable_gross_profit_display',
  );
  const userContext = useUserContext();
  const { selectedRestaurantRelationship, commissionSettings } = userContext;
  const ignoreNegativesInTotalCalculation =
    selectedRestaurantRelationship.salesRepIgnoreNegativeInGrossProfitTotal;
  const [styles] = useStyles();
  const theme = useTheme();

  // Get employee UUID from authenticated user context
  const employeeUUID = isAuthenticatedUserContext(userContext)
    ? userContext.user.uuid
    : '';

  // Fetch estimated commission from backend
  const {
    estimatedCommissionInMicros,
    loading: commissionLoading,
    refetch,
  } = useGetOrderEstimatedCommission(
    order.uuid,
    employeeUUID,
    partnerSupplier.businessOrganizationUUID,
    commissionSettings,
  );

  const [grossProfit, grossProfitPercent, totalGrossProfitInMicros] =
    useMemo(() => {
      if (isUpdating) {
        return ['--', '--', null] as const;
      }

      let totalGrossProfitInMicros = 0;
      let totalPriceInMicros = 0;

      const variantPackQuantityMap = order.orders_items.reduce<{
        [variantPackUUID: string]: number;
      }>((result, oi) => {
        if (typeof oi.order_item_prices?.pack_quantity_at_order === 'number') {
          result[oi.variants_pack.uuid] =
            oi.order_item_prices.pack_quantity_at_order;
        }
        return result;
      }, {});

      for (const [variantPackUUID, quantity] of Object.entries(
        variantPackQuantityMap,
      )) {
        const variantPackInfo = getVariantPackInfo(variantPackUUID);
        const salesRepInfo = variantPackInfo?.salesRepInfo;

        const priceInMicros =
          salesRepInfo?.salesRepPriceInMicros ?? variantPackInfo?.priceInMicros;
        const unitCount = variantPackInfo?.unitCount;

        const costMap = salesRepInfo?.costMap || {};
        const costInMicros = getVariantPackCostFromMap(
          costMap,
          selectedRestaurantRelationship.customerCostType,
        );

        if (
          typeof priceInMicros !== 'number' ||
          typeof costInMicros !== 'number' ||
          typeof unitCount !== 'number'
        ) {
          // Early return if any variant pack doesn't have data
          return ['--', '--', null] as const;
        }

        const grossProfitInMicros =
          (priceInMicros - costInMicros) * quantity * unitCount;

        // Don't add it to the total if it's negative and distributor wants to ignore negatives
        if (ignoreNegativesInTotalCalculation && grossProfitInMicros < 0) {
          continue;
        }

        totalGrossProfitInMicros += grossProfitInMicros;
        totalPriceInMicros += priceInMicros * quantity * unitCount;
      }

      return [
        formatCurrency(totalGrossProfitInMicros, currencyCode),
        // Show '--' instead of 'NaN%' if totalPriceInMicros is 0
        totalPriceInMicros === 0
          ? '--'
          : formatPercent(totalGrossProfitInMicros / totalPriceInMicros),
        totalGrossProfitInMicros,
      ] as const;
    }, [
      currencyCode,
      getVariantPackInfo,
      ignoreNegativesInTotalCalculation,
      isUpdating,
      order.orders_items,
      selectedRestaurantRelationship.customerCostType,
    ]);

  useEffect(() => {
    // Re-fetch estimated commission whenever gross profit changes
    if (
      totalGrossProfitInMicros !== null &&
      refetch &&
      commissionSettings?.draft_order_commission_enabled
    ) {
      refetch();
    }
  }, [totalGrossProfitInMicros, refetch, commissionSettings]);

  return (
    <View style={styles.base} testID="sales-rep-order-summary">
      {showGrossProfit ? (
        <View style={styles.itemSpacing}>
          {isUpdating ? (
            <Skeleton
              borderRadius={8}
              count={1}
              height={14}
              style={styles.skeletonText}
              width="80%"
            />
          ) : (
            <>
              <Typography
                color={theme.sk.color.text.primary}
                variant="paragraph-md"
              >
                {i18n.t('order_summary.gp')}
                {':'}
              </Typography>
              <Typography
                color={theme.sk.color.text.primary}
                variant="paragraph-md"
              >
                {[grossProfitPercent, grossProfit].join(' • ')}
              </Typography>
            </>
          )}
        </View>
      ) : null}
      {/* Commission block */}
      {commissionSettings?.draft_order_commission_enabled ? (
        <View style={[styles.itemSpacing, styles.commissionAligning]}>
          {/* Loading state for commission loading or cart being updated */}
          {commissionLoading || isUpdating ? (
            <Skeleton
              borderRadius={8}
              count={1}
              height={14}
              style={styles.skeletonText}
              width="80%"
            />
          ) : estimatedCommissionInMicros !== null ? (
            <>
              <Typography
                color={theme.sk.color.text.primary}
                variant="paragraph-md"
              >
                {i18n.t('order_summary.estimated_commission')}
                {':'}
              </Typography>
              <Typography
                color={theme.sk.color.text.primary}
                variant="paragraph-md"
              >
                {formatCurrency(estimatedCommissionInMicros, currencyCode)}
              </Typography>
            </>
          ) : null}
        </View>
      ) : null}
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  base: {
    backgroundColor: theme.colors.tileColor,
    borderRadius: 4,
    flexDirection: 'row',
    paddingVertical: 4,
  },
  commissionAligning: {
    justifyContent: 'flex-end',
  },
  itemSpacing: {
    columnGap: 4,
    flex: 1,
    flexDirection: 'row',
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  skeletonText: {
    marginVertical: 4,
  },
}));
